import { useStore } from '@stores';
import { InvoicePayment, TerminalMode } from '@services';
import { useCallback, useEffect } from 'react';
import { UseCardPaymentReturn } from '@models';


export const useCardPayment = (): UseCardPaymentReturn => {
  const { paymentStore } = useStore();

  useEffect(() => {
    return () => {
      paymentStore.cleanup();
    };
  }, [paymentStore]);

  const processCardPayment = useCallback(async (
    invoiceId: string, 
    amount: number, 
    terminalMode: TerminalMode,
    onSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => {
    return await paymentStore.processCardPayment(invoiceId, amount, terminalMode, onSuccess);
  }, [paymentStore]);

  const setCashPaymentLoading = useCallback((loading: boolean) => {
    paymentStore.setCashPaymentLoading(loading);
  }, [paymentStore]);

  const resetPaymentState = useCallback(() => {
    paymentStore.resetPaymentState();
  }, [paymentStore]);

  const resumeActiveAttempt = useCallback((
    invoiceId: string,
    onSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => paymentStore.resumeActiveAttempt(invoiceId, onSuccess), [paymentStore]);

  const resolveUnconfirmed = useCallback(
    (paidOnTerminal: boolean) => paymentStore.resolveUnconfirmed(paidOnTerminal),
    [paymentStore]
  );

  const cancelCardPayment = useCallback(() => paymentStore.cancelCardPayment(), [paymentStore]);

  return {
    processingPayment: paymentStore.processingPayment,
    processingStatus: paymentStore.processingStatus,
    paymentFinished: paymentStore.paymentFinished,
    cardPaymentLoading: paymentStore.cardPaymentLoading,
    cashPaymentLoading: paymentStore.cashPaymentLoading,
    hasPaymentError: paymentStore.hasPaymentError,
    signatureRequired: paymentStore.signatureRequired,
    requiresConfirmation: paymentStore.requiresConfirmation,
    isReconnecting: paymentStore.isReconnecting,
    resolvingConfirmation: paymentStore.resolvingConfirmation,
    cancellingCardPayment: paymentStore.cancellingCardPayment,
    cancelRequested: paymentStore.cancelRequested,
    processCardPayment,
    resumeActiveAttempt,
    resolveUnconfirmed,
    cancelCardPayment,
    setCashPaymentLoading,
    resetPaymentState,
    trackingId: paymentStore.trackingId,
    verifySignature: paymentStore.verifySignature,
  };
};